import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Dropdown, Flex, MenuProps } from 'antd';
import { TbCheck, TbProgress, TbSettings } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { taskService } from '@/services';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { TaskStatus, TaskStatusSystemKey } from '@/types';

import ManageTaskStatusesModal from '../ManageTaskStatusesModal';
import TaskStatusIcon from '../TaskStatusIcon';

interface TaskStatusDropdownProps {
    children?: ReactElement;
    disabled?: boolean;
    manageEnabled?: boolean;
    onChange: (status: TaskStatus) => Promise<void> | void;
    onOpenChange?: (open: boolean) => void;
    placeholder?: string;
    placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
    selectDefault?: boolean;
    size?: 'small' | 'middle';
    value?: TaskStatus | null;
    valueId?: string | null;
}

const TaskStatusDropdown: FC<TaskStatusDropdownProps> = ({
    children,
    disabled,
    manageEnabled,
    onChange,
    onOpenChange,
    placeholder = 'Status...',
    placement = 'bottomLeft',
    selectDefault = true,
    size = 'small',
    value,
    valueId,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const [statuses, setStatuses] = useState<TaskStatus[]>([]);

    const loadStatuses = useCallback(async () => {
        try {
            setLoading(true);
            setStatuses(await taskService.statuses.list());
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadStatuses();
    }, [loadStatuses]);

    const selectedStatus = useMemo(
        () =>
            value ??
            statuses.find((status) => status.id === valueId) ??
            (selectDefault ? statuses.find((status) => status.systemKey === TaskStatusSystemKey.TODO) : undefined),
        [selectDefault, statuses, value, valueId],
    );
    const items: MenuProps['items'] = [
        ...statuses.map((status) => ({
            key: status.id,
            label: (
                <Flex align='center' gap='0.5rem'>
                    <TaskStatusIcon status={status} />
                    <span>{status.name}</span>
                </Flex>
            ),
            extra: status.id === selectedStatus?.id ? <TbCheck style={{ color: token.colorPrimary }} /> : undefined,
            onClick: () => void onChange(status),
        })),
        ...(manageEnabled
            ? [
                  { type: 'divider' as const },
                  {
                      icon: <TbSettings size='1rem' />,
                      key: 'manage-statuses',
                      label: 'Manage Task Statuses',
                      onClick: () => setManageOpen(true),
                  },
              ]
            : []),
    ];

    return (
        <>
            <Dropdown
                disabled={disabled || loading}
                popupRender={() => (
                    <DropdownPanel
                        contentStyle={{ minHeight: '9rem' }}
                        footer={<LearnMoreFooter topic='task statuses and workflow' variant='dropdown' />}
                        header={<DropdownMenuLabel icon={<TbProgress size='0.875rem' />}>Status</DropdownMenuLabel>}
                        minWidth='14rem'
                    >
                        {statuses.length ? (
                            <DropdownPanelMenu items={items} />
                        ) : (
                            <EmptyView
                                description='Task statuses will appear here when they are available.'
                                height='9rem'
                                icon={<TbProgress size='1rem' />}
                                label='No Task Statuses'
                            />
                        )}
                    </DropdownPanel>
                )}
                onOpenChange={onOpenChange}
                placement={placement}
                trigger={['click']}
            >
                {children ?? (
                    <DropdownButton
                        disabled={disabled}
                        loading={loading}
                        size={size}
                        style={{ alignItems: 'center', display: 'inline-flex' }}
                    >
                        <TaskStatusIcon status={selectedStatus} />
                        <span>{selectedStatus?.name ?? placeholder}</span>
                    </DropdownButton>
                )}
            </Dropdown>
            {manageEnabled && (
                <ManageTaskStatusesModal
                    onChanged={() => void loadStatuses()}
                    onClose={() => setManageOpen(false)}
                    open={manageOpen}
                />
            )}
        </>
    );
};

export default TaskStatusDropdown;
